import { useState } from "react";
import { Bell, BellOff, BellRing, Loader2, Volume2, VolumeX, X } from "lucide-react";
import { toast } from "sonner";
import { disablePush, enablePush, pushPermission, pushSupported } from "../../connect/push";
import { playTone, setSoundEnabled, soundEnabled, soundLocked } from "../../connect/sound";
import { useConnect } from "../../connect/store";

/** Banner asking to turn on device notifications (shown while permission is still "default"). */
export const PushBanner = () => {
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("nxc_push_dismissed") === "1");
  const [busy, setBusy] = useState(false);
  const [perm, setPerm] = useState(pushPermission());
  if (!pushSupported() || perm !== "default" || hidden) return null;
  const enable = async () => {
    setBusy(true);
    try { await enablePush(); setPerm("granted"); toast.success("Notifications enabled on this device"); } catch (e) { toast.error(e.message); setPerm(pushPermission()); } finally { setBusy(false); }
  };
  return (
    <div className="mx-3 mt-3 nxc-card !border-blue-500/30 !bg-blue-500/[0.08] p-3 flex items-center gap-3 nxc-fade-in" data-testid="push-banner">
      <span className="w-9 h-9 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-300 shrink-0"><BellRing size={17} /></span>
      <div className="flex-1 min-w-0 text-xs text-slate-300">Get alerts for new messages even when the app is closed.</div>
      <button onClick={enable} disabled={busy} className="nxc-btn nxc-btn-primary h-8 text-xs shrink-0" data-testid="push-enable-button">{busy ? <Loader2 size={12} className="animate-spin" /> : "Turn on"}</button>
      <button onClick={() => { sessionStorage.setItem("nxc_push_dismissed", "1"); setHidden(true); }} className="text-slate-500 hover:text-slate-300 p-1" data-testid="push-banner-dismiss" aria-label="Dismiss"><X size={14} /></button>
    </div>
  );
};

/** Alert tone: on/off + tap-and-hold preview (CEO also previews the puppet alert). */
export const SoundToggle = () => {
  const { me } = useConnect();
  const [on, setOn] = useState(soundEnabled());
  const locked = soundLocked();
  const toggle = () => {
    if (locked) { toast("Alert tone stays on during working hours (9:30 AM – 5:30 PM)"); return; }
    const next = !on; setSoundEnabled(next); setOn(next); if (next) playTone("message", true); toast(next ? "Alert tone on" : "Alert tone muted");
  };
  if (locked) return (
    <span className="flex gap-1">
      <span className="nxc-btn nxc-btn-ghost h-9 text-xs text-amber-300 cursor-default" title="Alert tone cannot be muted 9:30 AM – 5:30 PM on working days" data-testid="sound-locked-badge"><Volume2 size={14} /> <span className="hidden sm:inline">On · work hours</span></span>
      {me?.is_ceo && <button onClick={() => playTone("ceo", true)} className="nxc-btn nxc-btn-ghost h-9 text-xs text-slate-400" data-testid="sound-preview-ceo" title="Preview CEO alert tone">CEO tone</button>}
    </span>
  );
  return (
    <span className="flex gap-1">
      <button onClick={toggle} className={`nxc-btn nxc-btn-ghost h-9 text-xs ${on ? "text-amber-300" : "text-slate-400"}`} data-testid="sound-toggle-button" title={on ? "Alert tone on — tap to mute (vibration stays on)" : "Alert tone muted — vibration still on"}>{on ? <Volume2 size={14} /> : <VolumeX size={14} />}</button>
      {me?.is_ceo && <button onClick={() => playTone("ceo", true)} className="nxc-btn nxc-btn-ghost h-9 text-xs text-slate-400" data-testid="sound-preview-ceo" title="Preview CEO alert tone">CEO tone</button>}
    </span>
  );
};

/** Header toggle: shows current push state and lets the user turn it on/off. */
export const PushToggle = () => {
  const [perm, setPerm] = useState(pushPermission());
  const [on, setOn] = useState(() => localStorage.getItem("nxc_push_off") !== "1");
  const [busy, setBusy] = useState(false);
  if (!pushSupported()) return null;
  const active = perm === "granted" && on;
  const click = async () => {
    setBusy(true);
    try {
      if (active) { await disablePush(); localStorage.setItem("nxc_push_off", "1"); setOn(false); toast("Device notifications turned off"); }
      else { await enablePush(); localStorage.removeItem("nxc_push_off"); setOn(true); setPerm("granted"); toast.success("Device notifications turned on"); }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <button onClick={click} disabled={busy} className={`nxc-btn nxc-btn-ghost h-9 text-xs ${active ? "text-emerald-300" : "text-slate-400"}`} data-testid="push-toggle-button" title={active ? "Device notifications on" : "Turn on device notifications"}>
      {busy ? <Loader2 size={14} className="animate-spin" /> : active ? <Bell size={14} /> : <BellOff size={14} />} {active ? "On" : "Off"}
    </button>
  );
};
